import React from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCheck, LuTreePalm, LuTriangleAlert } from 'react-icons/lu';
import { useTranslation } from '../../../i18n/LanguageContext';
import { describeVisit, leaveText } from '../../ProfilePage/calendar/CalendarEntries';
import DayAgenda from '../../ProfilePage/calendar/DayAgenda';
import { dateKey, jobDayNumber, jobState } from '../../ProfilePage/calendar/calendarDates';
import { JOB_STATE_TONES, PROJECT_STRIPES, SCREEN_FAIL_TONE, VISIT_STATE_TONES } from '../../ProfilePage/calendar/calendarTones';

// Bir sütunda penceresi açık vizitlerden en fazla bu kadarı gösterilir
const WINDOW_PREVIEW = 2;

/** A visit in a week column: number, patient, project and the day in its window. */
const WeekVisit = ({ visit, date, subtle, projectName, projectSlot, onOpen }) => {
  const { t } = useTranslation();
  const info = describeVisit(t, visit, date, projectName);
  const tone = visit.screenFail ? SCREEN_FAIL_TONE : VISIT_STATE_TONES[info.state];
  return (
    <li className="tw-block">
      <button
        type="button"
        title={info.tooltip}
        onClick={() => onOpen(visit)}
        className={`tw-w-full tw-rounded-lg tw-border-l-[3px] tw-px-2 tw-py-1.5 tw-text-left tw-transition-[filter] hover:tw-brightness-95 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:hover:tw-brightness-125 ${PROJECT_STRIPES[projectSlot]} ${
          subtle ? 'tw-border tw-border-dashed tw-border-slate-300 tw-bg-white/50 tw-text-slate-600 dark:tw-border-white/15 dark:tw-bg-white/[0.03] dark:tw-text-slate-300' : tone.chip
        }`}
      >
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-leading-tight">
          {info.number && <span className="tw-shrink-0 tw-rounded tw-bg-white/70 tw-px-1 tw-text-[10px] tw-font-bold tw-tabular-nums dark:tw-bg-black/25">{info.number}</span>}
          <span className="tw-min-w-0 tw-flex-1 tw-truncate">{info.patient}</span>
          {info.state === 'completedDay' && <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" strokeWidth={3} />}
          {info.pending.length > 0 && <LuTriangleAlert aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0 tw-text-amber-600 dark:tw-text-amber-300" />}
        </span>
        {projectName && <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[11px] tw-opacity-80">{projectName}</span>}
        <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[10px] tw-opacity-70">
          {info.dayLabel}{info.time ? ` · ${info.time}` : ''}
        </span>
      </button>
    </li>
  );
};

WeekVisit.propTypes = {
  visit: PropTypes.object.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  subtle: PropTypes.bool,
  projectName: PropTypes.string,
  projectSlot: PropTypes.number.isRequired,
  onOpen: PropTypes.func.isRequired,
};

/** A job in a week column. */
const WeekJob = ({ job, date, onOpen }) => {
  const { t } = useTranslation();
  const state = jobState(job);
  return (
    <li className="tw-block">
      <button
        type="button"
        title={job.title}
        onClick={() => onOpen(job)}
        className={`tw-w-full tw-rounded-lg tw-border tw-border-dashed tw-border-slate-300 tw-bg-white/70 tw-px-2 tw-py-1.5 tw-text-left tw-transition-colors hover:tw-bg-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:tw-border-white/15 dark:tw-bg-white/[0.04] dark:hover:tw-bg-white/[0.08] ${JOB_STATE_TONES[state].text}`}
      >
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-leading-tight">
          {job.status ? <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" strokeWidth={3} /> : <LuBriefcase aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />}
          <span className={`tw-min-w-0 tw-flex-1 tw-truncate ${job.status ? 'tw-line-through tw-decoration-1' : ''}`}>{job.title}</span>
        </span>
        <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[10px] tw-opacity-80">
          {t('profilePage.calendar.jobDay', { n: jobDayNumber(job, date) })} · {t(`profilePage.calendar.jobStatus.${job.status ? 'done' : 'ongoing'}`)}
        </span>
      </button>
    </li>
  );
};

WeekJob.propTypes = { job: PropTypes.object.isRequired, date: PropTypes.instanceOf(Date).isRequired, onOpen: PropTypes.func.isRequired };

/**
 * Seven days side by side (narrow screens: one under the other). A day's
 * planned visits and jobs are solid; visits whose window is merely open that
 * day are dashed. The day header opens the day view.
 */
const WeekView = ({ weekDates, dayData, columns, onOpenVisit, onOpenJob, onOpenDay, projectSlotOf, projectNameOf }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`operations.calendar.${key}`, vars);
  const todayKey = dateKey(new Date());

  if (!columns) {
    return (
      <div className="tw-space-y-5">
        {weekDates.map((date) => {
          const { entries, windowVisits, leaves } = dayData(date);
          const empty = entries.length === 0 && windowVisits.length === 0 && leaves.length === 0;
          return (
            <div key={dateKey(date)} className="tw-space-y-2">
              <DayAgenda
                date={date}
                entries={entries}
                windowVisits={windowVisits}
                leaves={leaves}
                showEmpty={false}
                canAdd={false}
                onOpenVisit={onOpenVisit}
                onOpenJob={onOpenJob}
                projectSlotOf={projectSlotOf}
                projectNameOf={projectNameOf}
              />
              {empty && <p className="tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">{label('noEntries')}</p>}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="tw-grid tw-grid-cols-7 tw-gap-2">
      {weekDates.map((date) => {
        const key = dateKey(date);
        const isToday = key === todayKey;
        const { entries, windowVisits, leaves } = dayData(date);
        const hiddenWindows = Math.max(0, windowVisits.length - WINDOW_PREVIEW);
        return (
          <section
            key={key}
            aria-label={date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
            className={`tw-flex tw-min-h-[14rem] tw-min-w-0 tw-flex-col tw-gap-2 tw-rounded-2xl tw-border tw-p-2 ${
              isToday
                ? 'tw-border-teal-500/40 tw-bg-teal-500/[0.05] dark:tw-border-teal-400/30 dark:tw-bg-teal-400/[0.06]'
                : leaves.length > 0
                  ? 'tw-border-sky-500/25 tw-bg-sky-500/[0.05] dark:tw-border-sky-400/20'
                  : 'tw-border-slate-200 tw-bg-white/60 dark:tw-border-white/10 dark:tw-bg-white/[0.02]'
            }`}
          >
            <button
              type="button"
              onClick={() => onOpenDay(date)}
              title={label('openDay')}
              className="tw-flex tw-items-center tw-justify-between tw-gap-1 tw-rounded-xl tw-bg-transparent tw-px-1.5 tw-py-1 tw-text-left tw-transition-colors hover:tw-bg-slate-900/[0.04] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:hover:tw-bg-white/[0.05]"
            >
              <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">
                {date.toLocaleDateString(locale, { weekday: 'short' })}
              </span>
              <span className={`tw-grid tw-h-7 tw-min-w-[1.75rem] tw-place-items-center tw-rounded-full tw-px-1 tw-text-sm tw-font-semibold tw-tabular-nums ${isToday ? 'tw-bg-teal-600 tw-text-white' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>
                {date.getDate()}
              </span>
            </button>

            {leaves.length > 0 && (
              <span title={leaveText(t, leaves)} className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1 tw-rounded-lg tw-bg-sky-500/15 tw-px-1.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-sky-800 dark:tw-text-sky-200">
                <LuTreePalm aria-hidden="true" className="tw-h-3 tw-w-3 tw-shrink-0" />
                <span className="tw-truncate">{leaveText(t, leaves)}</span>
              </span>
            )}

            {entries.length > 0 && (
              <ul className="tw-space-y-1.5">
                {entries.map((entry) => (entry.kind === 'visit' ? (
                  <WeekVisit
                    key={`v-${entry.item.id}`}
                    visit={entry.item}
                    date={date}
                    projectName={projectNameOf(entry.item.projectId)}
                    projectSlot={projectSlotOf(entry.item.projectId)}
                    onOpen={onOpenVisit}
                  />
                ) : (
                  <WeekJob key={`j-${entry.item.id}`} job={entry.item} date={date} onOpen={onOpenJob} />
                )))}
              </ul>
            )}

            {windowVisits.length > 0 && (
              <div className="tw-space-y-1.5">
                <p className="tw-px-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 dark:tw-text-slate-500">
                  {label('openWindows', { count: windowVisits.length })}
                </p>
                <ul className="tw-space-y-1.5">
                  {windowVisits.slice(0, WINDOW_PREVIEW).map((visit) => (
                    <WeekVisit
                      key={`w-${visit.id}`}
                      visit={visit}
                      date={date}
                      subtle
                      projectName={projectNameOf(visit.projectId)}
                      projectSlot={projectSlotOf(visit.projectId)}
                      onOpen={onOpenVisit}
                    />
                  ))}
                </ul>
                {hiddenWindows > 0 && (
                  <button
                    type="button"
                    onClick={() => onOpenDay(date)}
                    className="tw-rounded-md tw-bg-transparent tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-teal-700 hover:tw-bg-teal-500/10 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/60 dark:tw-text-teal-300"
                  >
                    {t('profilePage.calendar.more', { count: hiddenWindows })}
                  </button>
                )}
              </div>
            )}

            {entries.length === 0 && windowVisits.length === 0 && leaves.length === 0 && (
              <p className="tw-mt-auto tw-pb-1 tw-text-center tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{label('noEntries')}</p>
            )}
          </section>
        );
      })}
    </div>
  );
};

WeekView.propTypes = {
  weekDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  dayData: PropTypes.func.isRequired,
  columns: PropTypes.bool,
  onOpenVisit: PropTypes.func.isRequired,
  onOpenJob: PropTypes.func.isRequired,
  onOpenDay: PropTypes.func.isRequired,
  projectSlotOf: PropTypes.func.isRequired,
  projectNameOf: PropTypes.func.isRequired,
};

export default WeekView;
